import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import OrderForm from '../../components/OrderForm';
import { formatCurrency, formatDate, ORDER_STATUS_COLORS, ORDER_STATUS_LABELS } from '../../utils/roles';

const CashierPage = () => {
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [invoiceForm, setInvoiceForm] = useState({
    customer_name: '', customer_nit: '', has_tax: true, payment_method: 'cash'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    api.get('/orders').then(res => {
      const all = res.data.data.data || [];
      // El cajero ve los pedidos apenas se generan (no espera a que cocina los marque "listo"),
      // solo se excluyen los ya facturados (con factura vigente) o cancelados.
      setOrders(all.filter(o => (!o.invoice || o.invoice.status === 'cancelled') && o.status !== 'cancelled'));
      if (selectedOrder) {
        const stillThere = all.find(o => o.id === selectedOrder.id && (!o.invoice || o.invoice.status === 'cancelled'));
        if (!stillThere) setSelectedOrder(null);
      }
    });
    api.get('/invoices').then(res => setInvoices(res.data.data.data || []));
  };

  const createInvoice = async () => {
    if (!selectedOrder) return;
    setLoading(true);
    try {
      const res = await api.post('/invoices', {
        order_id: selectedOrder.id,
        ...invoiceForm,
      });
      const newInvoiceId = res.data.data?.id;
      setSelectedOrder(null);
      setInvoiceForm({ customer_name: '', customer_nit: '', has_tax: true, payment_method: 'cash' });
      fetchData();
      if (newInvoiceId) {
        await printTicket(newInvoiceId);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Error al emitir factura');
    } finally {
      setLoading(false);
    }
  };

  // Recibe el ID de la factura, pide el detalle completo (con el "ticket" armado)
  // y recién ahí abre la ventana de impresión.
  const printTicket = async (invoiceId) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}`);
      const ticket = res.data.data?.ticket;
      if (!ticket) {
        alert('No se pudo obtener el detalle de la factura');
        return;
      }

      const ticketWindow = window.open('', '_blank', 'width=320,height=600');
      ticketWindow.document.write(`
        <html>
          <head><title>Ticket ${ticket.invoice_number}</title></head>
          <body style="font-family: monospace; font-size: 12px; padding: 10px; width: 280px;">
            <center><b>${ticket.header}</b></center>
            <center>${ticket.date}</center>
            <hr>
            <p><b>Factura:</b> ${ticket.invoice_number}</p>
            <p><b>Cliente:</b> ${ticket.customer}</p>
            <p><b>NIT:</b> ${ticket.nit}</p>
            <p><b>Cajero:</b> ${ticket.cashier}</p>
            <hr>
            ${ticket.items.map(item => `
              <p>${item.quantity}x ${item.product}</p>
              ${item.modifiers.length ? `<p style="margin-left:10px">${item.modifiers.join(', ')}</p>` : ''}
              <p style="text-align:right">$${item.subtotal.toFixed(2)}</p>
            `).join('')}
            <hr>
            <p>Subtotal: $${ticket.subtotal.toFixed(2)}</p>
            ${ticket.has_tax ? `<p>IVA: $${ticket.tax.toFixed(2)}</p>` : '<p>SIN IVA (CF)</p>'}
            <p><b>TOTAL: $${ticket.total.toFixed(2)}</b></p>
            <hr>
            <center><i>${ticket.footer}</i></center>
          </body>
        </html>
      `);
      ticketWindow.document.close();
      ticketWindow.print();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al obtener el ticket');
    }
  };

  return (
    <div>
      <h1 style={{ color: '#e2e8f0', marginBottom: 24, fontSize: 28 }}>💰 Caja y Facturación</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { key: 'new', label: 'Nuevo Pedido', icon: '📝' },
          { key: 'orders', label: 'Pedidos por Cobrar', icon: '📋' },
          { key: 'invoices', label: 'Facturas Emitidas', icon: '🧾' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '12px 24px', borderRadius: 10, border: 'none',
              background: activeTab === tab.key ? '#10b981' : '#1e293b',
              color: activeTab === tab.key ? '#0f172a' : '#94a3b8',
              fontWeight: 600, cursor: 'pointer', fontSize: 14,
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'new' && (
        <OrderForm showWaiterSelect onOrderCreated={fetchData} />
      )}

      {activeTab === 'orders' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 24 }}>
          {/* Lista de pedidos */}
          <div>
            <h3 style={{ color: '#94a3b8', marginBottom: 16, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1 }}>
              Pedidos por cobrar
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {orders.map(order => (
                <div
                  key={order.id}
                  onClick={() => {
                    setSelectedOrder(order);
                    setInvoiceForm({
                      customer_name: order.customer_name || '',
                      customer_nit: '',
                      has_tax: true,
                      payment_method: 'cash',
                    });
                  }}
                  className="card"
                  style={{
                    cursor: 'pointer',
                    border: selectedOrder?.id === order.id ? '2px solid #10b981' : '1px solid #334155',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#38bdf8', fontWeight: 700 }}>{order.order_number}</span>
                    <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: 18 }}>
                      {formatCurrency(order.total)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                    <p style={{ color: '#94a3b8', fontSize: 13 }}>
                      {order.table ? `🪑 Mesa ${order.table.number}` : '📦 ' + order.order_type} ·
                      {order.items?.length} items · {formatDate(order.created_at)}
                    </p>
                    <span style={{
                      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      background: ORDER_STATUS_COLORS[order.status] + '20',
                      color: ORDER_STATUS_COLORS[order.status],
                    }}>
                      {ORDER_STATUS_LABELS[order.status]}
                    </span>
                  </div>
                  {order.user && (
                    <p style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>👤 Mesero: {order.user.name}</p>
                  )}
                </div>
              ))}
              {orders.length === 0 && (
                <p style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>No hay pedidos por cobrar</p>
              )}
            </div>
          </div>

          {/* Formulario de factura */}
          <div className="card" style={{ position: 'sticky', top: 24, height: 'fit-content' }}>
            <h3 style={{ color: '#e2e8f0', marginBottom: 16 }}>🧾 Emitir Factura</h3>

            {selectedOrder ? (
              <>
                <div style={{ background: '#0f172a', borderRadius: 10, padding: 12, marginBottom: 16 }}>
                  <p style={{ color: '#38bdf8', fontWeight: 700 }}>{selectedOrder.order_number}</p>
                  <p style={{ color: '#94a3b8', fontSize: 13 }}>
                    {selectedOrder.items?.length} items · Total: {formatCurrency(selectedOrder.total)}
                  </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Nombre cliente</label>
                    <input
                      type="text"
                      className="input"
                      value={invoiceForm.customer_name}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_name: e.target.value })}
                      placeholder="Consumidor Final"
                    />
                  </div>
                  <div>
                    <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>NIT/CI</label>
                    <input
                      type="text"
                      className="input"
                      value={invoiceForm.customer_nit}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, customer_nit: e.target.value })}
                      placeholder="CF"
                    />
                  </div>
                  <div>
                    <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Método de pago</label>
                    <select
                      className="input"
                      value={invoiceForm.payment_method}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, payment_method: e.target.value })}
                    >
                      <option value="cash">💵 Efectivo</option>
                      <option value="card">💳 Tarjeta</option>
                      <option value="transfer">🏦 Transferencia</option>
                      <option value="qr">📱 QR</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="has_tax"
                      checked={invoiceForm.has_tax}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, has_tax: e.target.checked })}
                      style={{ width: 18, height: 18 }}
                    />
                    <label htmlFor="has_tax" style={{ color: '#e2e8f0', fontSize: 14 }}>
                      Incluir IVA ({selectedOrder.branch?.tax_rate || 13}%)
                    </label>
                  </div>

                  <div style={{
                    background: '#0f172a', borderRadius: 10, padding: 16,
                    marginTop: 8, border: '1px solid #334155',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ color: '#94a3b8' }}>Subtotal</span>
                      <span style={{ color: '#e2e8f0' }}>{formatCurrency(selectedOrder.subtotal)}</span>
                    </div>
                    {invoiceForm.has_tax && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ color: '#94a3b8' }}>IVA</span>
                        <span style={{ color: '#e2e8f0' }}>{formatCurrency(selectedOrder.tax_amount)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #334155', paddingTop: 8 }}>
                      <span style={{ color: '#e2e8f0', fontWeight: 700 }}>TOTAL</span>
                      <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: 18 }}>
                        {formatCurrency(invoiceForm.has_tax ? selectedOrder.total : selectedOrder.subtotal)}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={createInvoice}
                    className="btn btn-success"
                    style={{ width: '100%', padding: 14, fontSize: 16, marginTop: 8 }}
                    disabled={loading}
                  >
                    {loading ? 'Procesando...' : '💰 Cobrar e Imprimir'}
                  </button>
                </div>
              </>
            ) : (
              <p style={{ color: '#64748b', textAlign: 'center', padding: 30 }}>Selecciona un pedido de la lista</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'invoices' && (
        /* Facturas emitidas */
        <div>
          <h3 style={{ color: '#94a3b8', marginBottom: 16, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1 }}>
            Historial de facturas
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {invoices.map(inv => (
              <div key={inv.id} className="card animate-fade-in">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ color: '#10b981', fontWeight: 700 }}>{inv.invoice_number}</span>
                  <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: 16 }}>{formatCurrency(inv.total)}</span>
                </div>
                <p style={{ color: '#94a3b8', fontSize: 13 }}>
                  {formatDate(inv.created_at)} · {inv.customer_name || 'Consumidor Final'} ·
                  {inv.has_tax ? ' Con IVA' : ' Sin IVA'}
                </p>
                <button
                  onClick={() => printTicket(inv.id)}
                  className="btn btn-primary"
                  style={{ padding: '6px 16px', fontSize: 12, marginTop: 10 }}
                >
                  🖨️ Reimprimir Ticket
                </button>
              </div>
            ))}
            {invoices.length === 0 && (
              <p style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>No hay facturas emitidas</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CashierPage;